"use client";

import Button, { geistSans } from "@/components/atoms/buttons/Button";
import PromoInput from "@/components/atoms/input/PromoInput";
import { cn } from "@/lib/utils";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Check, LoaderCircle } from "lucide-react";
import { isFilled, RichTextField } from "@prismicio/client";
import { PrismicRichText } from "@prismicio/react";
import { useState } from "react";
import { toast } from "@/components/atoms/toast";
import { __ } from "@/lib/i18n/functions";
import { Locales } from "@/lib/i18n";
import { createPromoBannerSchema, PromoBannerSchema } from "@/lib/validation/homepage";

export type PromoBannerProps = {
  promo: {
    promoTitle: string;
    promoDescription: RichTextField;
    buttonText: string;
  };
  success: {
    successTitle: string;
    successDescription: string;
  };
  state?: "default" | "success";
  locale: Locales;
};

const PromoBanner = ({ promo, success, state = "default", locale }: PromoBannerProps) => {
  const [isApiSuccess, setIsApiSuccess] = useState(false);
  const { promoBannerSchema } = createPromoBannerSchema(locale);
  const form = useForm<PromoBannerSchema>({
    resolver: zodResolver(promoBannerSchema),
    defaultValues: {
      email: "",
    },
  });

  const {
    control,
    handleSubmit,
    getValues,
    formState: { isSubmitting },
  } = form;

  const onSubmit = async (data: PromoBannerSchema) => {
    try {
      const response = await fetch(
        `${process.env.NEXT_PUBLIC_MEDUSA_BACKEND_URL}/store/newsletter/${encodeURIComponent(data.email)}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-publishable-api-key": process.env.NEXT_PUBLIC_MEDUSA_PUBLISHABLE_KEY || "",
          },
        }
      );

      if (response.status === 201) {
        setIsApiSuccess(true);
      } else {
        console.error("Failed to subscribe", await response.json());
        toast.error({
          title: __("Something went wrong. Please try again.", locale),
        });
        setIsApiSuccess(false);
      }
    } catch (error) {
      console.error("An error occurred:", error);
      toast.error({
        title: __("Something went wrong. Please try again.", locale),
      });
      setIsApiSuccess(false);
    }
  };

  const showSuccess = state === "success" || isApiSuccess;

  const highlightedText = promo.promoTitle.split(" ").pop();
  const titleText = promo.promoTitle.split(" ").slice(0, -1).join(" ");

  return (
    <div
      className={cn(
        "bg-festi-pink-50 flex h-full min-h-[17.625rem] w-full flex-1 flex-col justify-between gap-5 rounded-2xl px-6.5 py-8.5 lg:max-w-[48.625rem]",
        geistSans.className
      )}
    >
      {!showSuccess ? (
        <>
          <header>
            <h2 className="text-dark-primary font-cabinet-grotesk md:text-4.5xl text-3xl font-bold md:font-medium">
              {titleText} <span className="text-pink-primary">{highlightedText}</span>
            </h2>
            {isFilled.richText(promo.promoDescription) && (
              <div className="text-dark-primary mt-2 text-sm font-medium">
                <PrismicRichText field={promo.promoDescription} />
              </div>
            )}
          </header>
          <Form {...form}>
            <form onSubmit={handleSubmit(onSubmit)} className="flex w-full items-stretch gap-2.5">
              <FormField
                control={control}
                name="email"
                render={({ field }) => (
                  <FormItem className="w-full">
                    <FormMessage className="text-pink-primary" />
                    <FormLabel className="sr-only">Input</FormLabel>
                    <div className="flex w-full flex-col items-stretch gap-2.5 md:flex-row">
                      <FormControl className="flex w-full grow">
                        <PromoInput
                          placeholder={__("Email Address", locale)}
                          className="bg-white-primary h-full grow"
                          {...field}
                        />
                      </FormControl>
                      <Button
                        disabled={isSubmitting}
                        type="submit"
                        variant="primary"
                        iconRightName={!isSubmitting ? "ArrowRight" : undefined}
                        className="h-auto sm:h-[60px]"
                      >
                        {isSubmitting ? <LoaderCircle className="h-5 w-5 animate-spin" /> : promo.buttonText}
                      </Button>
                    </div>
                  </FormItem>
                )}
              />
            </form>
          </Form>
        </>
      ) : (
        <div className="space-y-5">
          <Check className="text-pink-primary" aria-hidden="true" size={72} />
          <div className="w-full space-y-3.5 md:space-y-2">
            <h2 className="text-dark-primary md:text-4.5xl text-3xl font-bold md:font-medium">
              {success.successTitle}
            </h2>
            <p className="text-dark-primary text-sm">
              {success.successDescription} : {getValues("email") || "hello@tomaklod.design"}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default PromoBanner;
